// src/components/Dashboard_ADM/Atendimento/ConversasTab.tsx
// Caixa de conversas do agente WhatsApp — seção 8.1 do relatório CRM v3.0
// GET /whatsapp-agent/conversations | GET /whatsapp-agent/conversations/:id
// POST /whatsapp-agent/conversations/:id/assumir|devolver|messages
// DELETE /whatsapp-agent/conversations/:id

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  MessageCircle, RefreshCw, Search, Loader2, User, Bot, UserCog,
  Send, ArrowLeftRight, Clock, AlertTriangle, Phone, FileText, Trash2,
  Volume2, VolumeX,
} from 'lucide-react';
import apiClient from '../../../services/api';

// ─── Types ───────────────────────────────────────────────────────────────────
type ConversationAgentId =
  | 'orchestrator' | 'capilar' | 'unhas' | 'estetica' | 'terapias'
  | 'gestao' | 'pagamento' | 'pos-venda';

type ConversationStatus = 'pendente' | 'em_atendimento' | 'escalada' | 'lida' | 'resolvida';

interface Conversation {
  id: string;
  whatsappNumber: string;
  leadName: string | null;
  affiliateId: string | null;
  currentAgent: ConversationAgentId;
  status: ConversationStatus;
  assumedByUserId: string | null;
  failedAttempts: number;
  escalationSummary: Record<string, any> | null;
  lastMessageAt: string | null;
  createdAt: string;
}

interface Message {
  id: string;
  sender: 'lead' | 'agent' | 'human';
  agentId: ConversationAgentId | null;
  content: string;
  mediaUrl: string | null;
  createdAt: string;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────
const AGENT_LABELS: Record<ConversationAgentId, string> = {
  orchestrator: 'Orquestrador',
  capilar: 'Área Capilar',
  unhas: 'Área de Unhas',
  estetica: 'Estética',
  terapias: 'Terapias',
  gestao: 'Gestão e Arte',
  pagamento: 'Pagamento',
  'pos-venda': 'Pós-venda',
};

const STATUS_CONFIG: Record<ConversationStatus, { label: string; dot: string; text: string }> = {
  pendente:       { label: 'Pendente',        dot: 'bg-amber-400',  text: 'text-amber-700'  },
  em_atendimento: { label: 'Em atendimento',  dot: 'bg-blue-400',   text: 'text-blue-700'   },
  escalada:       { label: 'Escalada',        dot: 'bg-red-500',    text: 'text-red-700'    },
  lida:           { label: 'Lida',            dot: 'bg-gray-400',   text: 'text-gray-700'   },
  resolvida:      { label: 'Resolvida',       dot: 'bg-green-400',  text: 'text-green-700'  },
};

function formatDateTime(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('pt-BR');
}

function guessMediaKind(url: string): 'image' | 'audio' | 'video' | 'other' {
  const clean = url.split('?')[0].toLowerCase();
  if (/\.(png|jpe?g|gif|webp)$/.test(clean)) return 'image';
  if (/\.(mp3|ogg|oga|wav|m4a|opus)$/.test(clean)) return 'audio';
  if (/\.(mp4|mov|webm)$/.test(clean)) return 'video';
  return 'other';
}

const MediaPreview: React.FC<{ url: string }> = ({ url }) => {
  const kind = guessMediaKind(url);
  if (kind === 'image') {
    return <img src={url} alt="mídia" className="rounded-xl max-w-full max-h-64 mb-2" />;
  }
  if (kind === 'audio') {
    return <audio controls src={url} className="max-w-full mb-2" />;
  }
  if (kind === 'video') {
    return <video controls src={url} className="rounded-xl max-w-full max-h-64 mb-2" />;
  }
  return (
    <a href={url} target="_blank" rel="noreferrer"
      className="flex items-center gap-2 text-sm underline mb-2 opacity-90 hover:opacity-100">
      <FileText className="w-4 h-4" /> Ver arquivo
    </a>
  );
};

// ─── Componente ───────────────────────────────────────────────────────────────
const ConversasTab: React.FC = () => {
  const [conversas, setConversas]   = useState<Conversation[]>([]);
  const [loading, setLoading]       = useState(true);
  const [busca, setBusca]           = useState('');
  const [filtroStatus, setFiltroStatus] = useState('');
  const [salvando, setSalvando]     = useState(false);

  const [detalheId, setDetalheId]     = useState<string | null>(null);
  const [detalheConv, setDetalheConv] = useState<Conversation | null>(null);
  const [mensagens, setMensagens]     = useState<Message[]>([]);
  const [loadingDetalhe, setLoadingDetalhe] = useState(false);
  const [textoResposta, setTextoResposta]   = useState('');
  const [somAtivo, setSomAtivo]             = useState(false);

  const audioContextRef = useRef<AudioContext | null>(null);
  const somAtivoRef = useRef(false);
  const conversasConhecidasRef = useRef<Map<string, string>>(new Map());
  const listaInicializadaRef = useRef(false);

  const tocarNotificacao = useCallback(() => {
    const context = audioContextRef.current;
    if (!context || context.state !== 'running') return;

    // Dois tons curtos e agradaveis, gerados pelo navegador (sem arquivo externo).
    const tocarTom = (frequencia: number, inicio: number) => {
      const oscillator = context.createOscillator();
      const gain = context.createGain();
      oscillator.type = 'sine';
      oscillator.frequency.value = frequencia;
      gain.gain.setValueAtTime(0.0001, inicio);
      gain.gain.exponentialRampToValueAtTime(0.18, inicio + 0.015);
      gain.gain.exponentialRampToValueAtTime(0.0001, inicio + 0.18);
      oscillator.connect(gain);
      gain.connect(context.destination);
      oscillator.start(inicio);
      oscillator.stop(inicio + 0.2);
    };

    const agora = context.currentTime;
    tocarTom(660, agora);
    tocarTom(880, agora + 0.16);
  }, []);

  const alternarSom = async () => {
    if (somAtivoRef.current) {
      somAtivoRef.current = false;
      setSomAtivo(false);
      return;
    }

    const AudioContextClass = window.AudioContext
      || (window as typeof window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AudioContextClass) {
      alert('Este navegador não oferece suporte à notificação sonora.');
      return;
    }

    const context = audioContextRef.current ?? new AudioContextClass();
    audioContextRef.current = context;
    if (context.state === 'suspended') await context.resume();
    somAtivoRef.current = true;
    setSomAtivo(true);
    tocarNotificacao();
  };

  // ── Fetch lista ────────────────────────────────────────────────────────────
  const fetchConversas = useCallback(async (
    options: { notificar?: boolean; mostrarLoading?: boolean } = {},
  ) => {
    const { notificar = false, mostrarLoading = true } = options;
    if (mostrarLoading) setLoading(true);
    try {
      const data = await apiClient.get<Conversation[]>('/whatsapp-agent/conversations');
      const lista = data ?? [];

      if (listaInicializadaRef.current && notificar) {
        const existeNovidade = lista.some(conversa => {
          const anterior = conversasConhecidasRef.current.get(conversa.id);
          return !anterior || (conversa.lastMessageAt ?? '') > anterior;
        });
        if (existeNovidade && somAtivoRef.current) tocarNotificacao();
      }

      conversasConhecidasRef.current = new Map(
        lista.map(conversa => [conversa.id, conversa.lastMessageAt ?? '']),
      );
      listaInicializadaRef.current = true;
      setConversas(lista);
    } catch {
      // Em atualizacoes silenciosas, preserva a lista visivel durante falhas temporarias.
      if (mostrarLoading) setConversas([]);
    } finally {
      if (mostrarLoading) setLoading(false);
    }
  }, [tocarNotificacao]);

  useEffect(() => { fetchConversas(); }, [fetchConversas]);

  useEffect(() => {
    const intervalo = window.setInterval(() => {
      void fetchConversas({ notificar: true, mostrarLoading: false });
    }, 10_000);
    return () => window.clearInterval(intervalo);
  }, [fetchConversas]);

  useEffect(() => () => {
    if (audioContextRef.current) void audioContextRef.current.close();
  }, []);

  // ── Fetch detalhe ──────────────────────────────────────────────────────────
  const abrirDetalhe = async (id: string) => {
    setDetalheId(id);
    setLoadingDetalhe(true);
    try {
      const data = await apiClient.get<{ conversation: Conversation; messages: Message[] }>(
        `/whatsapp-agent/conversations/${id}`,
      );
      setDetalheConv(data.conversation);
      setMensagens(data.messages ?? []);
    } catch {
      setDetalheConv(null);
      setMensagens([]);
    } finally {
      setLoadingDetalhe(false);
    }
  };

  const fecharDetalhe = () => {
    setDetalheId(null);
    setDetalheConv(null);
    setMensagens([]);
    setTextoResposta('');
  };

  // ── Ações ──────────────────────────────────────────────────────────────────
  const handleAssumir = async (id: string) => {
    setSalvando(true);
    try {
      await apiClient.post(`/whatsapp-agent/conversations/${id}/assumir`, {});
      await fetchConversas();
      if (detalheId === id) await abrirDetalhe(id);
    } catch { alert('Erro ao assumir a conversa'); }
    finally { setSalvando(false); }
  };

  const handleDevolver = async (id: string) => {
    setSalvando(true);
    try {
      await apiClient.post(`/whatsapp-agent/conversations/${id}/devolver`, {});
      await fetchConversas();
      if (detalheId === id) await abrirDetalhe(id);
    } catch { alert('Erro ao devolver a conversa para a IA'); }
    finally { setSalvando(false); }
  };

  const handleEnviarResposta = async () => {
    if (!detalheId || !textoResposta.trim()) return;
    setSalvando(true);
    try {
      await apiClient.post(`/whatsapp-agent/conversations/${detalheId}/messages`, { text: textoResposta.trim() });
      setTextoResposta('');
      await abrirDetalhe(detalheId);
    } catch { alert('Erro ao enviar mensagem'); }
    finally { setSalvando(false); }
  };

  const handleExcluirConversa = async () => {
    if (!detalheId) return;

    const identificacao = detalheConv?.leadName || detalheConv?.whatsappNumber || 'este contato';
    const confirmou = window.confirm(
      `Excluir a conversa de ${identificacao}?\n\nTodo o histórico será apagado e o próximo contato começará um novo atendimento. Esta ação não pode ser desfeita.`,
    );
    if (!confirmou) return;

    setSalvando(true);
    try {
      await apiClient.delete(`/whatsapp-agent/conversations/${detalheId}`);
      fecharDetalhe();
      await fetchConversas();
    } catch {
      alert('Erro ao excluir a conversa');
    } finally {
      setSalvando(false);
    }
  };

  // ── Filtro local ───────────────────────────────────────────────────────────
  const conversasFiltradas = useMemo(() => {
    return conversas.filter(c => {
      const alvo = `${c.leadName ?? ''} ${c.whatsappNumber}`.toLowerCase();
      const mb = !busca || alvo.includes(busca.toLowerCase());
      const ms = !filtroStatus || c.status === filtroStatus;
      return mb && ms;
    });
  }, [conversas, busca, filtroStatus]);

  const stats = useMemo(() => ({
    total: conversas.length,
    pendentes: conversas.filter(c => c.status === 'pendente').length,
    emAtendimento: conversas.filter(c => c.status === 'em_atendimento').length,
    escaladas: conversas.filter(c => c.status === 'escalada').length,
  }), [conversas]);

  return (
    <div className="space-y-6">
      {/* HEADER */}
      <div className="bg-white rounded-2xl border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-black text-gray-900 mb-1 flex items-center gap-3">
              <MessageCircle className="w-6 h-6 text-blue-600" /> Conversas WhatsApp
            </h2>
            <p className="text-gray-600">Caixa unificada do agente de atendimento por nicho</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={alternarSom}
              title={somAtivo ? 'Desativar som de novas mensagens' : 'Ativar som de novas mensagens'}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl border-2 font-bold transition-colors ${
                somAtivo
                  ? 'border-green-300 bg-green-50 text-green-700 hover:bg-green-100'
                  : 'border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}>
              {somAtivo ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}
              <span className="hidden sm:inline">{somAtivo ? 'Som ativo' : 'Ativar som'}</span>
            </button>
            <button onClick={() => void fetchConversas()}
              title="Atualizar conversas"
              className="flex items-center gap-2 px-4 py-2 rounded-xl border-2 border-gray-300 text-gray-700 font-bold hover:bg-gray-50">
              <RefreshCw className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: 'Total',          value: stats.total,         bg: 'bg-gray-50',  border: 'border-gray-200'  },
            { label: 'Pendentes',      value: stats.pendentes,     bg: 'bg-amber-50', border: 'border-amber-200' },
            { label: 'Em atendimento', value: stats.emAtendimento, bg: 'bg-blue-50',  border: 'border-blue-200'  },
            { label: 'Escaladas',      value: stats.escaladas,     bg: 'bg-red-50',   border: 'border-red-200'   },
          ].map(s => (
            <div key={s.label} className={`${s.bg} rounded-xl p-4 border ${s.border}`}>
              <p className="text-xs text-gray-600 mb-1">{s.label}</p>
              <p className="text-2xl font-black text-gray-900">{s.value}</p>
            </div>
          ))}
        </div>
      </div>

      {/* LISTA */}
      <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
        <div className="p-5 border-b border-gray-200">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="relative md:col-span-2">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input type="text" value={busca} onChange={e => setBusca(e.target.value)}
                placeholder="Buscar por nome ou WhatsApp…"
                className="w-full pl-12 pr-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 font-semibold" />
            </div>
            <select value={filtroStatus} onChange={e => setFiltroStatus(e.target.value)}
              className="px-4 py-3 border-2 border-gray-300 rounded-xl text-gray-900 font-semibold">
              <option value="">Todos os Status</option>
              {Object.entries(STATUS_CONFIG).map(([k, v]) => (
                <option key={k} value={k}>{v.label}</option>
              ))}
            </select>
          </div>
        </div>

        {loading ? (
          <div className="flex flex-col items-center justify-center py-20 gap-3">
            <Loader2 className="w-10 h-10 text-blue-600 animate-spin" />
            <p className="text-gray-500 font-semibold">Carregando conversas…</p>
          </div>
        ) : conversasFiltradas.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20">
            <MessageCircle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-600 font-black text-lg">Nenhuma conversa encontrada</p>
            <p className="text-gray-400 text-sm mt-1">
              As conversas aparecem aqui assim que o agente WhatsApp começar a atender leads
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {conversasFiltradas.map(conv => {
              const st = STATUS_CONFIG[conv.status] ?? STATUS_CONFIG.pendente;
              return (
                <button key={conv.id} onClick={() => abrirDetalhe(conv.id)}
                  className="w-full text-left p-5 hover:bg-gray-50 transition-colors">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center flex-shrink-0">
                      <User className="w-5 h-5 text-blue-700" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-1">
                        <p className="font-bold text-gray-900">{conv.leadName || 'Lead sem nome'}</p>
                        <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-gray-100 text-gray-700 flex items-center gap-1">
                          <Phone className="w-3 h-3" /> {conv.whatsappNumber}
                        </span>
                        <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-indigo-100 text-indigo-700">
                          {AGENT_LABELS[conv.currentAgent] ?? conv.currentAgent}
                        </span>
                        <span className="flex items-center gap-1 text-xs">
                          <span className={`w-2 h-2 rounded-full ${st.dot}`} />
                          <span className={`${st.text} font-semibold`}>{st.label}</span>
                        </span>
                        {conv.assumedByUserId && (
                          <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-purple-100 text-purple-700 flex items-center gap-1">
                            <UserCog className="w-3 h-3" /> Assumida
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-400 flex items-center gap-1">
                        <Clock className="w-3 h-3" /> Última mensagem: {formatDateTime(conv.lastMessageAt)}
                      </p>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* MODAL DETALHE */}
      {detalheId && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[85vh] flex flex-col">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
              <div>
                <h3 className="text-xl font-black text-gray-900 flex items-center gap-2">
                  <MessageCircle className="w-5 h-5 text-blue-600" />
                  {detalheConv?.leadName || 'Lead sem nome'}
                </h3>
                <p className="text-sm text-gray-500">{detalheConv?.whatsappNumber}</p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleExcluirConversa}
                  disabled={salvando || loadingDetalhe}
                  title="Excluir conversa e reiniciar atendimento"
                  className="flex items-center gap-2 px-3 py-2 rounded-xl border border-red-200 text-red-600 font-bold hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {salvando ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                  <span className="hidden sm:inline">Excluir</span>
                </button>
                <button onClick={fecharDetalhe} disabled={salvando}
                  className="text-gray-400 hover:text-gray-600 text-2xl disabled:opacity-50">✕</button>
              </div>
            </div>

            {loadingDetalhe ? (
              <div className="flex-1 flex items-center justify-center py-16">
                <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
              </div>
            ) : (
              <>
                {detalheConv?.status === 'escalada' && detalheConv.escalationSummary && (
                  <div className="mx-6 mt-4 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-800 flex items-start gap-2 flex-shrink-0">
                    <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-bold">Conversa escalada — {detalheConv.escalationSummary.motivo_escalonamento}</p>
                      <p className="text-red-700 mt-1">{detalheConv.escalationSummary.historico_resumido}</p>
                    </div>
                  </div>
                )}

                <div className="flex-1 overflow-y-auto p-6 space-y-3">
                  {mensagens.length === 0 ? (
                    <p className="text-center text-gray-400 py-8">Nenhuma mensagem ainda</p>
                  ) : mensagens.map(msg => {
                    const isLead = msg.sender === 'lead';
                    const isHuman = msg.sender === 'human';
                    return (
                      <div key={msg.id} className={`flex ${isLead ? 'justify-start' : 'justify-end'}`}>
                        <div className={`max-w-[75%] rounded-2xl px-4 py-2.5 ${
                          isLead ? 'bg-gray-100 text-gray-900'
                          : isHuman ? 'bg-purple-600 text-white'
                          : 'bg-blue-600 text-white'
                        }`}>
                          <p className="text-xs opacity-70 mb-1 flex items-center gap-1">
                            {isLead ? <User className="w-3 h-3" /> : isHuman ? <UserCog className="w-3 h-3" /> : <Bot className="w-3 h-3" />}
                            {isLead ? 'Lead' : isHuman ? 'Atendente' : AGENT_LABELS[msg.agentId as ConversationAgentId] ?? 'Agente'}
                          </p>
                          {msg.mediaUrl && <MediaPreview url={msg.mediaUrl} />}
                          <p className="whitespace-pre-wrap text-sm">{msg.content}</p>
                          <p className="text-[10px] opacity-60 mt-1">{formatDateTime(msg.createdAt)}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="p-4 border-t border-gray-200 flex-shrink-0 space-y-3">
                  {detalheConv?.assumedByUserId ? (
                    <>
                      <div className="flex gap-2">
                        <input value={textoResposta} onChange={e => setTextoResposta(e.target.value)}
                          onKeyDown={e => e.key === 'Enter' && handleEnviarResposta()}
                          placeholder="Digite a resposta ao lead…"
                          className="flex-1 px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 text-gray-900" />
                        <button onClick={handleEnviarResposta} disabled={salvando || !textoResposta.trim()}
                          className="px-4 py-3 rounded-xl bg-blue-600 text-white font-bold hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2">
                          {salvando ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
                        </button>
                      </div>
                      <button onClick={() => handleDevolver(detalheId)} disabled={salvando}
                        className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border-2 border-gray-300 text-gray-700 font-bold hover:bg-gray-50 disabled:opacity-50">
                        <ArrowLeftRight className="w-4 h-4" /> Devolver para IA
                      </button>
                    </>
                  ) : (
                    <button onClick={() => handleAssumir(detalheId)} disabled={salvando}
                      className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-purple-600 text-white font-bold hover:bg-purple-700 disabled:opacity-50">
                      {salvando ? <Loader2 className="w-5 h-5 animate-spin" /> : <UserCog className="w-5 h-5" />} Assumir conversa
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ConversasTab;
